import { CommonModule } from '@angular/common';
import { Component, inject, input, OnDestroy, OnInit, output, signal, ViewChild } from '@angular/core';
import { BxDropdownGenericModule } from '@buildxact/ui-components/bx-dropdowns';
import { BxTableHeaderModule } from '@buildxact/ui-components/bx-table-header';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DatatableComponent, NgxDatatableModule } from '@siemens/ngx-datatable';
import moment from 'moment';
import { BxDropdownOptionsModule } from '../../../../core/components/bx-dropdown-options/bx-dropdown-options.component';
import { Page, TableFooterPagingModule } from '../../../../core/components/table-footer-paging/table-footer-paging.component';
import { BxLogger } from '../../../../core/services/bx-logger.service';
import { BxUnverifiedAlertService } from '../../../../core/services/bx-unverified-alert.service';
import { DialogService } from '../../../../core/services/dialog.service';
import { EstimatesV2Service } from '../../../../core/services/estimateV2.service';
import { LaunchDarklyService } from '../../../../core/services/launchdarkly.service';
import { PermissionService } from '../../../../core/services/permission';
import { StoresService } from '../../../../core/services/stores';
import { TablesHelperService } from '../../../../core/services/tables-helper.service';
import { RepoJobsService } from '../../../../data/repositories/repo-jobs.service';
import { RepoQuoteRequestsService } from '../../../../data/repositories/repo-quoterequests.service';
import { BreezeService } from '../../../../data/services/breeze/breeze.service';
import { SharedDataService } from '../../../../data/services/shared-data.service';
import { EstimateViewStoresService } from '../../../estimates/estimate-view/shell';
import { QuoteRequestsService } from '../../quoterequest.service';
import { UIQuoteRequestsRecipientsTableComponent } from '../ui-quoterequests-recipients-table/ui-quoterequests-recipients-table.component';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { QuoteRequestListModel } from '../../quoterequest-list/domain/entities/quote-request-list.model';
import { catchError, EMPTY, finalize, first, firstValueFrom } from 'rxjs';
import { JobPaymentFilterInput, QuoteRequestFilterInput, QuoteRequestSortInput, Uuid } from '../../../../../graphql';
import { QuoteRequestListApiService } from '../../quoterequest-list/domain/apis/quote-request-list-api.service';
import { SortEvent } from '../../../../data/models/datatable.model';
import { Estimate, Job } from '../../../../data/models';
import { InvoiceListModel } from '../../../jobs/invoices/domain/entities/invoice-list.model';

type ExpandableQuoteRequest = QuoteRequestListModel & { isRecipientExpanded: boolean };

@UntilDestroy()
@Component({
    selector: 'app-ui-quoterequests-table',
    styleUrls: ['./ui-quoterequests-table.component.scss'],
    templateUrl: './ui-quoterequests-table.component.html',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        TableFooterPagingModule,
        NgxDatatableModule,
        BxTableHeaderModule,
        BxDropdownGenericModule,
        BxDropdownOptionsModule,
        UIQuoteRequestsRecipientsTableComponent,
    ],
})
export class UIQuoteRequestsTableComponent implements OnInit, OnDestroy {
    #bxUnverifiedAlertService = inject(BxUnverifiedAlertService);
    #estimatesService = inject(EstimatesV2Service);
    #logger = inject(BxLogger);
    #estimateViewStoresService = inject(EstimateViewStoresService);
    #breezeService = inject(BreezeService);
    #storesService = inject(StoresService);
    #quoteRequestsService = inject(QuoteRequestsService);
    sharedData = inject(SharedDataService);
    permissionService = inject(PermissionService);
    #quoteReqRepo = inject(RepoQuoteRequestsService);
    #tablesHelperService = inject(TablesHelperService);
    #lds = inject(LaunchDarklyService);
    #repoJobs = inject(RepoJobsService);
    #dialogService = inject(DialogService);
    #route = inject(ActivatedRoute);
    #quoteRequestListApiService = inject(QuoteRequestListApiService);

    @ViewChild('myTable') table!: DatatableComponent;
    estimate = input<Estimate>();
    job = input<Job>();
    requestsLoaded = output<QuoteRequestListModel[]>();
    title = 'Quote Requests';
    editing = false;
    status?: string;
    rows: ExpandableQuoteRequest[] = [];
    isLoading = signal<boolean>(false);
    isRecipientsLoading = signal<boolean>(false);
    page: Page = new Page();
    sortField?: string = 'requestNumber';
    sortDirection: 'ASC' | 'DESC' = 'DESC';
    expanded: any = {};
    showingDeleted = false;
    search?: string;

    settingsActions = [
        {
            groupName: 'Display only',
            actions: [
                { label: 'Active Items', condition: true, value: 0 },
                { label: 'Deleted Items', condition: true, value: 1 },
            ],
        },
    ];

    ngOnInit() {
        this.title = `${this.showingDeleted ? 'Deleted ' : ''}Quote Requests`;
        this.page = new Page();
        this.setDefaults();
        this.setPage({ offset: 0 });
        this.loadData();

        this.#quoteRequestsService.expandRecipientId$.pipe(untilDestroyed(this)).subscribe((rfqId) => {
            const row = this.rows.find((row) => row.iD_QuoteRequest === rfqId);
            if (row) {
                this.expandRow(row);
            }
        });

        this.#estimateViewStoresService.refreshRfqStatus$.pipe(untilDestroyed(this)).subscribe(() => {
            this.loadData();
        });
    }

    ngOnDestroy(): void {
        this.cleanUpExpandFlag();
    }

    async recover(item: QuoteRequestListModel) {
        try {
            await this.#quoteRequestsService.recoverQuoteRequest(item);
            this.resetPageNumber();
            this.loadData();
            this.#storesService.refreshQuoteRequests();
        } catch {
            // cancelled
        }
    }

    async viewQuoteRequest(quoteRequest: ExpandableQuoteRequest) {
        this.editing = true;
        try {
            this.isLoading.set(true);
            await this.#quoteRequestsService.viewQuoteRequest(quoteRequest, this.estimate(), this.job());
            this.reloadQuoteRequest(quoteRequest);
        } catch {
            // cancelled
        } finally {
            this.isLoading.set(false);
        }
    }

    reloadQuoteRequest(quoteRequest: ExpandableQuoteRequest) {
        const take = 1;
        const skip = 0;
        const where: QuoteRequestFilterInput = {
            and: [{ iD_QuoteRequest: { eq: quoteRequest.iD_QuoteRequest } }],
        };
        this.#quoteRequestListApiService
            .getQuoteRequests(take, skip, null, null, null, where)
            .pipe(first())
            .subscribe((result) => {
                if (result.data?.quoteRequestsV2?.items?.length) {
                    const refreshedRow = {
                        ...result.data.quoteRequestsV2.items[0]!,
                        isRecipientExpanded: quoteRequest.isRecipientExpanded,
                    } as ExpandableQuoteRequest;
                    this.rows = this.rows.map((r) => (r.iD_QuoteRequest === quoteRequest.iD_QuoteRequest ? refreshedRow : r));
                } else {
                    // it's been deleted
                    this.rows = this.rows.filter((r) => r.iD_QuoteRequest !== quoteRequest.iD_QuoteRequest);
                }
            });
    }

    showDeletedView(isShowingDeleted: boolean) {
        this.showingDeleted = isShowingDeleted;
        this.title = `${this.showingDeleted ? 'Deleted ' : ''}Quote Requests`;
        this.#storesService.updateSubpanelFilterStatus({ submenu: 'request-for-quotes', enabled: !this.showingDeleted });
        // Reset to first page
        this.setDefaults();
        this.loadData();
    }

    calcExpiryDays(row: QuoteRequestListModel) {
        return row.dateDeadline ? moment(row.dateDeadline).from(moment()) : 9999999;
    }

    async sendQuoteRequest(quoteRequest: ExpandableQuoteRequest) {
        if (!this.sharedData.UserData.identityInfo.email_verified) {
            this.#bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }

        await this.#quoteReqRepo.Send(quoteRequest.iD_QuoteRequest);
        this.reloadQuoteRequest(quoteRequest);
    }

    async deleteQuoteRequest(evt: Event, quoteRequest: ExpandableQuoteRequest) {
        evt.stopPropagation();

        const params = {
            title: 'Delete Quote Request?',
            text: "You can recover this Quote Request by selecting 'Settings', then 'Show Deleted'.",
        };
        const notification = 'Quote Request has been deleted';

        try {
            await this.#dialogService.show(params);
            this.isLoading.set(true);
            const success = await firstValueFrom(this.#estimatesService.deleteQuoteRequest(quoteRequest.iD_QuoteRequest));
            if (success.data?.deleteQuoteRequest) {
                this.#logger.success(notification);
                this.resetPageNumber();
                this.loadData();
                this.#storesService.refreshQuoteRequests();
            } else {
                this.isLoading.set(false);
            }
        } catch {
            // cancelled
            this.isLoading.set(false);
        }
    }

    resolveSettingsAction(value: number) {
        this.search = '';
        switch (value) {
            case 0: {
                // active items
                this.showDeletedView(false);
                break;
            }
            case 1: {
                // deleted items
                this.showDeletedView(true);
                break;
            }
        }
    }

    setSearch(search: string | undefined) {
        this.search = search;
        this.loadData();
    }

    toggleExpandRow(row: ExpandableQuoteRequest) {
        if (this.table) {
            row.isRecipientExpanded = !row.isRecipientExpanded;
            this.table.rowDetail.toggleExpandRow(row);
        }
    }

    expandRow(row: ExpandableQuoteRequest) {
        if (!this.table || row.isRecipientExpanded) {
            return;
        }

        row.isRecipientExpanded = true;
        this.table.rowDetail.toggleExpandRow(row);
    }

    setPage(pageInfo: { offset: any }) {
        if (this.page.pageNumber !== pageInfo.offset) {
            this.page.pageNumber = pageInfo.offset;
            this.loadData();
        }
    }

    pageLimit(event: { target: { textContent: string } }) {
        this.page.size = Number.parseInt(event.target.textContent);
        this.page.pageNumber = 0;
        this.loadData();
    }

    setDefaults() {
        this.page.pageNumber = 0;
        this.page.size = 10;
    }

    loadData() {
        this.isLoading.set(true);
        this.status = this.#route.snapshot.queryParams.status;
        this.title = `${this.showingDeleted ? 'Deleted ' : ''}Quote Requests`;

        const take = this.page.size;
        const skip = this.page.pageNumber * this.page.size;
        let sort: QuoteRequestSortInput = {};
        if (this.sortField) {
            sort = { [this.sortField]: this.sortDirection };
        }

        let where: QuoteRequestFilterInput = { and: [] };
        where.and!.push({ isDeleted: { eq: this.showingDeleted } });

        if (this.estimate()) {
            where.and!.push({ iD_Estimate: { eq: this.estimate()!.ID_Estimate } });
        }

        if (this.job()) {
            where.and!.push({ iD_Job: { eq: this.job()!.ID_Job } });
        }

        this.#quoteRequestListApiService
            .getQuoteRequests(take, skip, this.search, this.status, [sort], where)
            .pipe(
                first(),
                catchError(() => {
                    return EMPTY;
                }),
                finalize(() => {
                    this.isLoading.set(false);
                })
            )
            .subscribe((result) => {
                if (result.data.quoteRequestsV2) {
                    this.page.totalElements = result.data.quoteRequestsV2.totalCount;
                    this.rows = result.data.quoteRequestsV2.items!.map((r) => {
                        return { ...r, isRecipientExpanded: false };
                    });
                    setTimeout(() => {
                        const rfqIds = this.rows ? this.rows.map((e) => e.iD_QuoteRequest) : [];
                        this.requestsLoaded.emit(this.rows);
                        this.rows.map((quoteRequest) => {
                            if (this.isDeadlineExtensionRequested(quoteRequest)) {
                                this.expandRow(quoteRequest);
                            }
                        });
                    }, 500);
                }
            });
    }

    private isDeadlineExtensionRequested(quoteRequest: ExpandableQuoteRequest): boolean {
        const deadline = moment(quoteRequest.dateDeadline);
        return (
            (deadline.isValid() &&
                deadline.diff(new Date()) <= 0 &&
                quoteRequest.quoteRequestRecipients?.some((qrr) => qrr?.extensionRequested)) ??
            false
        );
    }

    getSubmittedResponses(quoteRequest: ExpandableQuoteRequest): number {
        return (
            quoteRequest.quoteRequestRecipients?.filter((i) => {
                return i?.dateSubmitted;
            }).length ?? 0
        );
    }

    onSort(event: SortEvent<any>) {
        if (event.sorts.length) {
            const newSortField = event.sorts[0].prop.toString();
            const oldSortField = this.sortField;
            this.sortField = event.sorts[0].prop.toString();
            // For datatable, when switching to a different column for sorting,
            // it always defaults to DESC
            if (newSortField !== oldSortField) {
                this.sortDirection = 'DESC';
            } else {
                this.sortDirection = event.sorts[0].dir.toUpperCase() as 'ASC' | 'DESC';
            }
            this.loadData();
        }
    }

    onResize() {
        setTimeout(() => {
            this.rows = [...this.rows];
        }, 500);
    }

    private cleanUpExpandFlag() {
        if (this.rows) {
            this.rows.map((quoteRequest) => (quoteRequest.isRecipientExpanded = false));
        }
    }

    private resetPageNumber() {
        if (this.#tablesHelperService.isResetPageNumber(this.page.totalElements - 1, this.page.size, this.page.pageNumber))
            this.page.pageNumber--;
    }

    async createNewQuoteRequestInJob() {
        if (!this.job()) return;
        const j = this.job()!;
        await this.#quoteRequestsService
            .newQuoteRequest(
                {
                    ID_Estimate: j.ID_Estimate,
                    ID_Job: j.ID_Job,
                    CalculationMethod: j.CalculationMethod,
                    TaxRates: j.TaxRates,
                    items: [],
                },
                null,
                this.job
            )
            .catch(() => {
                // Must have catch otherwise the openAside.reject will throw uncaught error
            });

        this.loadData();
    }

    onRecipientsLoading(e: boolean) {
        this.isRecipientsLoading.set(e);
    }
}
